import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '../../hooks/useAuth';
import { useTelemetriaPageview } from '../../hooks/useTelemetriaPageview';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import CommandPalette from './CommandPalette';

interface OutletContextType {
  isFullscreen: boolean;
  setIsFullscreen: (value: boolean) => void;
}

const Layout: React.FC = () => {
  const { isAuthenticated, isLoading } = useIsAuthenticated();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useTelemetriaPageview();

  // Recolhimento da sidebar é só manual (botão « no Topbar) e persiste entre
  // navegações. O auto-recolher por rota (HEAVY_ROUTES) foi removido — o
  // abre/fecha sozinho causava confusão visual.

  // ⌘K / Ctrl+K abre/fecha o Command Palette (atalho global do shell).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Contexto para as páginas filhas
  const outletContext: OutletContextType = {
    isFullscreen,
    setIsFullscreen,
  };

  // useUser ainda resolvendo (lendo localStorage / chamando /perfil) — splash
  // pra evitar redirect prematuro pro /login enquanto o token é validado.
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-app text-sm text-tx-3">
        Carregando…
      </div>
    );
  }

  // Sem token: redireciona pro /login preservando o destino. /login fica fora
  // deste Layout (ver App.tsx), então este redirect nunca cai num loop.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <div className="flex h-screen bg-app">
      {/* Sidebar - esconder em fullscreen */}
      {!isFullscreen && (
        <Sidebar
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />
      )}

      {/* Área principal */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Topbar - esconder em fullscreen */}
        {!isFullscreen && (
          <Topbar
            collapsed={collapsed}
            onToggleCollapsed={() => setCollapsed((v) => !v)}
            onAbrirPalette={() => setPaletteOpen(true)}
            onAbrirMobileMenu={() => setMobileOpen(true)}
          />
        )}

        {/* Conteúdo principal */}
        <main className={`flex-1 overflow-y-auto bg-white dark:bg-zinc-950 ${
          isFullscreen
            ? 'p-0'
            : 'p-2 sm:p-4 md:p-6'
        }`}>
          <Outlet context={outletContext} />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
};

export default Layout;
